/**
 * Replaces the `{count}` token in a CMS-authored label with the given number.
 * When count is 1, the first plural word after the token (the first one ending
 * in "s") is singularized, so both "{count} Photos Selected" and
 * "{count} Selected Photos" read as "1 Photo ..." rather than "1 Photos ...".
 * Returns an empty string when no label is provided so callers can render
 * nothing rather than "undefined".
 * @param label - CMS label that may contain the `{count}` token
 * @param count - value to substitute for `{count}`
 */
export const interpolateCount = (label: string | undefined, count: number): string => {
  const withCount = (label ?? '').replace(/\{count\}/g, String(count));
  if (count !== 1) return withCount;

  // Only the portion after the number is singularized: a word before it is not
  // the noun the count governs.
  const countIndex = withCount.indexOf(String(count));
  if (countIndex < 0) return withCount;

  const head = withCount.slice(0, countIndex + String(count).length);
  const tail = withCount.slice(countIndex + String(count).length);

  return head + tail.replace(/\b(\w+?)s\b/, '$1');
};

/**
 * Replaces the `{photoName}` token in a CMS-authored label with the given
 * photo's alt text. Falls back to the alt text alone when no label is provided.
 * @param label - CMS label that may contain the `{photoName}` token
 * @param photoAlt - alt text of the photo to substitute for `{photoName}`
 */
export const interpolatePhotoName = (label: string | undefined, photoAlt: string): string =>
  label ? label.replace(/\{photoName\}/g, photoAlt) : photoAlt;
